import React, { createContext, useContext } from 'react';
import { FormControl, InputBase, Typography } from '@material-ui/core';
import { IOnboardingAccount, IOnboardingLaw, IOnboardingSetup, INamedEntity } from '../../../../../Api/AptorApi';
import { useStyles as useFieldStyles } from '../../../../../Components/Field/Field.styles';
import SelectField from '../../../../../Components/Field/SelectField';
import { TypedFormFieldValue } from '../../../../../Components/Form/Form.types';

export interface OnboardingDraft extends Omit<IOnboardingSetup, 'legislation' | 'notes' | 'requirementNotes'> {
  selectedLaws: Array<INamedEntity & { number: string }>;
  unitIds: number[];
  noteUnitIds: Record<number, number[]>;
  noteLawIds?: number[];
  noteCompanyUnitIds?: number[];
  noteText?: string;
  notes: Record<string, string>;
  requirementNoteRequirementIds?: number[];
  requirementNoteCompanyUnitIds?: number[];
  requirementNoteText?: string;
  requirementNotes: Record<string, string>;
  requirementNoteChanges: Record<string, string | null>;
  unitName: string;
  requirement: IOnboardingSetup['requirements'][number];
  editingUnitId?: number | null;
  requirementIdsToDelete?: number[];
}
export const emptyRequirement = (): OnboardingDraft['requirement'] => ({ name: '', area: 0, effectiveFrom: '', description: '', unitIds: [] });
export const OnboardingDraftContext = createContext<{
  draft: OnboardingDraft; update: (change: (current: OnboardingDraft) => OnboardingDraft) => void;
  account: IOnboardingAccount; languages: INamedEntity[]; areas: INamedEntity[];
}>(undefined as any);
export const useOnboardingDraft = () => useContext(OnboardingDraftContext);
export const noteKey = (lawId: number, unitId: number) => `${lawId}:${unitId}`;
export const requirementNoteKey = (requirementId: number, unitId: number) => `${requirementId}:${unitId}`;
export const hasText = (html: string) => !!html.replace(/<[^>]*>/g, '').replace(/&nbsp;|&#160;/g, ' ').trim();
export const requirementOptions = (draft: OnboardingDraft, account: IOnboardingAccount): Array<INamedEntity & { unitIds: number[] }> => {
  const byId = new Map<number, Array<(typeof account.otherRequirements)[number]>>();
  (account.otherRequirements || []).forEach(requirement => {
    const items = byId.get(requirement.id) || [];
    items.push(requirement);
    byId.set(requirement.id, items);
  });
  const deleted = new Set((draft.requirementIdsToDelete || []).map(Number));
  const editedById = new Map(draft.requirements.filter(requirement => requirement.id && requirement.id > 0).map(requirement => [requirement.id!, requirement]));
  return [
    ...Array.from(byId.entries()).filter(([id]) => !deleted.has(id)).map(([id, items]) => {
      const edited = editedById.get(id);
      return { id, name: edited?.name || items[0].name, unitIds: edited ? edited.unitIds : Array.from(new Set(items.map(item => item.companyUnitId))) };
    }),
    ...draft.requirements.filter(requirement => !requirement.id || requirement.id < 0).map(requirement => ({
      id: requirement.id || -1, name: requirement.name, unitIds: requirement.unitIds,
    })),
  ];
};
export const lawRows = (draft: OnboardingDraft): IOnboardingLaw[] => draft.selectedLaws.flatMap(law =>
  draft.units.map(unit => ({ ...law, companyUnitId: unit.id, companyUnitName: unit.name,
    note: draft.notes[noteKey(law.id, unit.id)] || draft.notes[String(law.id)] || '',
  })),
);
export const validRequirement = (item: OnboardingDraft['requirement']) => !!item.name.trim() && item.name.length <= 255 && item.area > 0 && !!item.effectiveFrom && hasText(item.description) && item.description.length <= 2000 && item.unitIds.length > 0 && (!item.effectiveTo || item.effectiveTo >= item.effectiveFrom);
export const validStep = (step: number, draft: OnboardingDraft, _account: IOnboardingAccount) => {
  switch (step) {
    case 0: return draft.units.length > 0 && draft.editingUnitId == null;
    case 6: return !!draft.admin.firstName.trim() && !!draft.admin.lastName.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(draft.admin.email) && draft.admin.language > 0;
    case 1: return draft.selectedLaws.length > 0;
    case 3: return true;
    case 4: return draft.requirements.every(item => validRequirement(item) && item.unitIds.every(id => draft.units.some(unit => unit.id === id)))
    case 5: return true;
    default: return draft.unitIds.every(id => draft.units.some(unit => unit.id === id));
  }
};
export const setupPayload = (draft: OnboardingDraft): IOnboardingSetup => ({
  units: draft.units, admin: draft.admin,
  legislation: draft.selectedLaws.length ? [{ lawIds: draft.selectedLaws.map(law => law.id), unitIds: draft.units.map(unit => unit.id) }] : [],
  notes: draft.selectedLaws.flatMap(law => (draft.noteUnitIds[law.id] || []).map(unitId => ({
    lawId: law.id, unitId, note: draft.notes[noteKey(law.id, unitId)] || draft.notes[String(law.id)] || '',
  }))),
  requirements: draft.requirements,
  requirementNotes: Object.entries(draft.requirementNoteChanges || {}).map(([key, note]) => {
    const [requirementId, unitId] = key.split(':').map(Number);
    return { requirementId, unitId, note: hasText(note || '') ? note : null };
  }),
  requirementIdsToDelete: draft.requirementIdsToDelete || [],
});

// Controlled fields update the draft immediately; Next never reads a delayed text value.
export const OnboardingDraftText = ({ label, value, onChange, type = 'text', required = true }: { label: string; value: string; onChange: (value: string) => void; type?: string; required?: boolean }) => {
  const classes = useFieldStyles();
  const id = React.useId();
  return <FormControl className={classes.formControl}>
    <Typography component="label" htmlFor={id}>{label}{required && '*'}</Typography>
    <InputBase id={id} type={type} value={value} onChange={event => onChange(event.target.value)} classes={{ focused: classes.focused, error: classes.error }} />
  </FormControl>;
};
export const OnboardingDraftSelect = ({ name, label, ids, options, onChange, multi = true, required = false }: {
  name: string; label: string; ids: number[]; options: INamedEntity[]; onChange: (ids: number[]) => void; multi?: boolean; required?: boolean;
}) => <SelectField key={name + ':' + ids.join(',')} name={name} inputId={name} label={label} isMulti={multi} required={required} errorTexts={[]}
  options={options.map(option => ({ value: option.id, label: option.name }))}
  initialState={multi ? ids.map(value => ({ value, label: options.find(option => option.id === value)?.name || String(value) })) : ids[0] ? { value: ids[0], label: options.find(option => option.id === ids[0])?.name || String(ids[0]) } : undefined}
  onChange={(value?: TypedFormFieldValue<number> | TypedFormFieldValue<number>[]) => onChange(Array.isArray(value) ? value.map(item => item.value) : value ? [value.value] : [])} />;
